import React, { useCallback, useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Toast } from "../../../shared/Toast";
import Loader from "../../../shared/Loader";
import API from "../../../services/api";

const UploadModal = ({ isOpen, onClose, creativeId, creativeType, onUploadSuccess, onUploadError }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [fileType, setFileType] = useState(null);

  useEffect(() => {
    if (!selectedFile) {
      setPreviewUrl("");
      setFileType(null);
      return;
    }
    const objectUrl = URL.createObjectURL(selectedFile);
    setPreviewUrl(objectUrl);
    
    // Determine file type
    if (selectedFile.type.startsWith("image/")) {
      setFileType("image");
    } else if (selectedFile.type.startsWith("video/")) {
      setFileType("video");
    }
    
    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedFile]);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length === 0) {
      Toast("error", "No valid files selected");
      return;
    }
    setSelectedFile(acceptedFiles[0]);
  }, []);

  const handlePaste = useCallback(
    (event) => {
      const items = event.clipboardData?.items;
      if (!items) return;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.kind === "file" && item.type.startsWith(creativeType)) {
          const blob = item.getAsFile();
          if (blob) {
            setSelectedFile(new File([blob], blob.name || "clipboard", { type: blob.type }));
            event.preventDefault();
            return;
          }
        }
      }
    },
    [creativeType]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("paste", handlePaste);
    } else {
      document.removeEventListener("paste", handlePaste);
    }

    return () => {
      document.removeEventListener("paste", handlePaste);
    };
  }, [isOpen, handlePaste]);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png", ".gif"],
      "video/*": [".mp4", ".avi", ".mov", ".wmv"],
    },
    multiple: false,
    disabled: uploading,
  });

  const handleUpload = async () => {
    if (!selectedFile) {
      Toast("error", "Please select a file first");
      return;
    }
    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      formData.append("files", selectedFile);
      formData.append("creative_id", creativeId);

      let uploadUrl;
      if (creativeType === "full_page" || creativeType === "full_image") {
        uploadUrl = "/upload/alphaImage";
      } else if (creativeType === "full_video" || creativeType === "half_video" || creativeType === "promoted_video_story") {
        uploadUrl = "/upload/alphaVideo";
      } else {
        uploadUrl = "/upload/alphaPromtedImage";
      }
      
      // For half_video, determine upload endpoint based on file type
      if (creativeType === "half_video") {
        if (fileType === "image") {
          uploadUrl = "/upload/alphaImage";
        } else if (fileType === "video") {
          uploadUrl = "/upload/alphaVideo";
        }
      }
      const result = await API({
        url: uploadUrl,
        method: "POST",
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (result.code === 200 || result.code === 204) {
        Toast("success", result.message || "Upload successful");
        onUploadSuccess && onUploadSuccess(result);
        setSelectedFile(null);
        setFileType(null);
        setPreviewUrl("");
        onClose();
      } else {
        Toast("error", result.data?.message || "Upload failed");
        onUploadError && onUploadError(result);
      }
    } catch (error) {
      console.error("Upload error:", error);
      Toast("error", "Upload failed. Please try again.");
      onUploadError && onUploadError(error);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Upload Design</h3>
          <button
            onClick={() => {
              setSelectedFile(null);
              setFileType(null);
              setPreviewUrl("");
              onClose();
            }}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={uploading}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-600 mb-2">
            Creative ID: <span className="font-medium">{creativeId}</span>
          </p>
          <p className="text-sm text-gray-600">
            Type: <span className="font-medium capitalize">{creativeType}</span>
          </p>
        </div>

        <div
          {...getRootProps()}
          tabIndex={0}
          className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
            isDragActive ? "border-blue-400 bg-blue-50" : isDragReject ? "border-red-400 bg-red-50" : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
          } ${uploading ? "opacity-50 cursor-not-allowed" : ""}`}
        >
          <input {...getInputProps()} />
          {uploading ? (
            <div className="space-y-4">
              <Loader />
              <p className="text-sm text-gray-600">Uploading...</p>
              {uploadProgress > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-600 h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div>
                <p className="text-sm text-gray-600">{isDragActive ? (isDragReject ? "File type not supported" : "Drop the file here") : "Drag & drop a file here, or click to select"}</p>
                <p className="text-xs text-gray-500 mt-1">Supported: JPG, PNG, GIF, MP4, AVI, MOV, WMV</p>
              </div>
            </div>
          )}
        </div>

        {previewUrl && fileType === "image" && (
          <div className="mt-4">
            <img src={previewUrl} alt="Preview" className="max-h-48 mx-auto rounded" />
          </div>
        )}

        {previewUrl && fileType === "video" && (
          <div className="mt-4">
            <video controls className="max-h-48 mx-auto rounded">
              <source src={previewUrl} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
        )}

        <div className="mt-6 flex justify-end space-x-3">
          <button 
            onClick={() => {
              setSelectedFile(null);
              setFileType(null);
              setPreviewUrl("");
              onClose();
            }} 
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200" 
            disabled={uploading}
          >
            Cancel
          </button>
          <button onClick={handleUpload} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700" disabled={uploading || !selectedFile}>
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadModal;
